import React from 'react';
import { Upload } from 'antd';
import type { UploadProps } from 'antd';
import type { UploadRef } from 'antd/es/upload';
import { InboxOutlined } from '@ant-design/icons';

export type FileUploadProps = Omit<UploadProps, 'customRequest'> & {
  /**
   * Promise-based alternative to antd's `customRequest`: upload the file,
   * report progress via `setProgress` (0-100) and throw to mark the upload
   * as failed — the error message is shown in the file item tooltip.
   * The resolved value is stored on `file.response`.
   */
  onRequest?: (file: File, setProgress: (percent: number) => void) => Promise<unknown>;
};

export const FileUpload = React.forwardRef<UploadRef, FileUploadProps>((
  {
    onRequest, children, type = 'drag', ...props
  },
  ref,
) => (
  <Upload
    ref={ref}
    type={type}
    // antd's Upload.Dragger defaults hasControlInside to false so the drag
    // trigger gets role="button" and tabIndex="0"; plain Upload defaults it
    // to true, which silently drops both. Mirror Dragger for the drag type.
    hasControlInside={type !== 'drag'}
    customRequest={onRequest && (({
      file, onProgress, onSuccess, onError,
    }) => {
      onRequest(file as File, (percent) => onProgress?.({ percent }))
        .then((response) => onSuccess?.(response))
        .catch((error) => onError?.(error));
    })}
    {...props}
  >
    {children ?? (type === 'drag' && (
      <>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag file to this area to upload</p>
      </>
    ))}
  </Upload>
));

FileUpload.displayName = 'FileUpload';
